import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Reusable KPI Card Component
 */
export function KPICard({ title, value, change, icon: Icon, color, iconColor, isCount = false, formatValue }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getChangeColor = (change) => {
    return change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600';
  };

  const getChangeIcon = (change) => {
    return change > 0 ? '↗' : change < 0 ? '↘' : '→';
  };

  const displayValue = formatValue 
    ? formatValue(value)
    : isCount 
      ? value.toLocaleString()
      : formatCurrency(value);

  return (
    <div className={`${color} border rounded-xl p-6 transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {displayValue}
          </p>
          {change !== undefined && (
            <div className="flex items-center mt-2">
              <span className={`text-sm font-medium ${getChangeColor(change)}`}>
                {getChangeIcon(change)} {formatPercentage(change)}
              </span>
              <span className="text-xs text-gray-500 ml-2">vs last period</span>
            </div>
          )}
        </div>
        <div className={`${iconColor} p-3 rounded-lg bg-white/50`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

/**
 * Reusable Quick Action Card Component
 */
export function QuickActionCard({ title, description, icon: Icon, link, onClick, color, iconColor }) {
  const cardContent = (
    <div className="flex items-start space-x-4">
      <div className={`${iconColor} p-3 rounded-lg bg-white/50 group-hover:scale-110 transition-transform duration-200`}>
        <Icon size={24} />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900 group-hover:text-gray-700">
          {title}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {description}
        </p>
      </div>
    </div>
  );

  const cardClasses = `${color} border rounded-xl p-6 transition-all duration-200 hover:shadow-md hover:scale-105 group cursor-pointer`;

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={cardClasses}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <Link
      to={link}
      className={cardClasses}
    >
      {cardContent}
    </Link>
  );
}

/**
 * Reusable Overview Page Layout Component
 */
export function OverviewPageLayout({ 
  title, 
  subtitle, 
  kpiCards = [], 
  quickActions = [], 
  summarySection,
  children 
}) {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
          <p className="mt-2 text-gray-600">{subtitle}</p>
        </div>
      </div>

      {/* KPI Cards */}
      {kpiCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiCards.map((kpi, index) => (
            <KPICard key={index} {...kpi} />
          ))}
        </div>
      )}

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => (
              <QuickActionCard key={index} {...action} />
            ))}
          </div>
        </div>
      )}

      {/* Summary Section */}
      {summarySection && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {summarySection}
        </div>
      )}

      {/* Additional Content */}
      {children}
    </div>
  );
}
